import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, CircularProgress, InputAdornment, IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, AccountCircle, Lock } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import ChangePasswordDialog from '../components/ChangePasswordDialog';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      // Check if user must change password from the result data
      if (result.userData && result.userData.mustChangePassword) {
        console.log('✅ Must change password - showing dialog');
        // Store user data temporarily for the password change dialog
        setTempUserData(result.userData);
        setMustChangePassword(true);
        setShowPasswordChange(true);
        // DO NOT navigate - stay on login page to show dialog
      } else {
        console.log('❌ No password change required, navigating to dashboard');
        // Navigate directly if no password change needed
        navigate('/');
      }
    } else {
      setError(result.message);
    }
  };

  const handlePasswordChanged = () => {
    // After password is changed, set the user in localStorage and navigate
    if (tempUserData) {
      const updatedUser = { ...tempUserData, mustChangePassword: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.href = '/'; // Force full page reload to re-authenticate
    }
    setShowPasswordChange(false);
    setMustChangePassword(false);
    setTempUserData(null);
  };

  return (
    <>
      <ChangePasswordDialog 
        open={showPasswordChange}
        onClose={handlePasswordChanged}
        isForced={mustChangePassword}
        tempUser={tempUserData}
      />
      <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              🎬 JBDE Digital Hub
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to access your content management portal
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username or Mobile Number"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              autoFocus
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                )
              }}
              placeholder="e.g., JBDEDinakaran or 9876543210"
              helperText="Enter your JBDE username or registered mobile number"
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>

            {['JBDESridhar', 'JBDESathiyaprabhu', 'JBDESumathi'].includes(username) && (
              <Box textAlign="center" mb={2}>
                <Button
                  component={Link}
                  to="/forgot-password"
                  variant="text"
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  Forgot Password?
                </Button>
              </Box>
            )}
          </form>

        </CardContent>
      </Card>
    </Box>
    </>
  );
}

export default Login;
